import { useAppConstantsContext } from '@/context/AppConstants';
import {
  DataCard,
  openReportInExistingTab,
  openReportInNewTab,
} from '@/reports/index';
import { SearchType } from '@/reports/openReport/openReport';
import { AddressFormat } from '@/reports/shared/AddressFormat/AddressFormat';
import {
  build as buildDataCardBadge,
  DataCardBadgeTruthinessMapping,
} from '@/reports/shared/DataCard/DataCardBadge/DataCardBadge';
import {
  build as buildTag,
  DataCardTagTruthinessMapping,
} from '@/reports/shared/DataCard/DataCardTag/DataCardTag';
import { serializedTypes } from '@/reports/types';
import { DataList, Text } from '@/theme/index';
import { getDatesSeen, nameize } from '@/utils/index';
import { isNewInfoItem } from '@/utils/isNewInfoItem/isNewInfoItem';

interface AddressCardProps {
  address: serializedTypes.Address;
  permalink: string;
  thumbRating: () => JSX.Element;
  ownerName?: string;
  showCityAndStateAddress?: boolean;
}

export const AddressCard = ({
  address,
  permalink,
  thumbRating,
  ownerName,
  showCityAndStateAddress = false,
}: AddressCardProps) => {
  const datesSeen = getDatesSeen(address);
  const { is_searchable } = address;
  const {
    links: { baseUrl },
    config: { enabledReportTypes = [], higherConfidenceThreshold },
    featureFlags: { shouldOpenReportInCurrentTab },
  } = useAppConstantsContext();
  let isAddressReportEnabled = true;
  if (enabledReportTypes.length > 0) {
    isAddressReportEnabled = enabledReportTypes.includes('address');
  }
  const isClickable =
    isAddressReportEnabled && is_searchable && !showCityAndStateAddress;

  const badge = () => {
    const dataConfidence = address?.meta?.confidence || 0;
    const owner = nameize(ownerName ?? '');

    const badgeMapping: DataCardBadgeTruthinessMapping[] = [
      {
        name: 'bestMatch',
        truthiness: address?.meta?.is_best || false,
        insert: 'address',
      },
      {
        name: 'higherConfidence',
        truthiness: dataConfidence >= higherConfidenceThreshold,
        insert: owner,
      },
    ];

    return buildDataCardBadge(badgeMapping);
  };

  const addressSearchParams = {
    address: address.parsed
      ? `${address.parsed?.primary_address_number} ${address.parsed?.street_name} ${address.parsed?.street_type} `
      : '',
    city: address.parsed?.city || '',
    state: address.parsed?.state || '',
    zipcode: address.parsed?.zip5 || '',
  };

  function handleClick() {
    const tabData = {
      ...addressSearchParams,
      searchType: 'property' as SearchType,
    };

    if (shouldOpenReportInCurrentTab) {
      openReportInExistingTab(tabData, baseUrl);
      return;
    }

    openReportInNewTab(tabData, baseUrl);
  }

  const addressCardTitle = (
    <Text
      variant="h2"
      color="success"
      sx={{
        marginBottom: 0,
        wordBreak: 'break-all',
        overflowWrap: 'break-word',
        textDecoration: isClickable ? 'underline' : 'none',
        '&:hover': {
          cursor: isClickable ? 'pointer' : 'default',
        },
      }}
      onClick={
        isClickable && addressSearchParams.zipcode ? handleClick : undefined
      }
    >
      <AddressFormat
        address={address}
        showCityAndStateAddress={showCityAndStateAddress}
      />
    </Text>
  );

  const tag = () => {
    const tagMapping: DataCardTagTruthinessMapping[] = [
      {
        name: 'newInfo',
        truthiness: isNewInfoItem({
          newInfo: address?.new_info,
          monitoredChangeType: address?.meta?.monitored_change_type,
          monitoredOn: address?.meta?.monitored_on,
        }),
      },
    ];

    return buildTag(tagMapping);
  };

  return (
    <DataCard
      badge={badge()}
      tag={tag()}
      thumbRating={thumbRating}
      permalink={permalink}
      buttonProps={{
        label:
          isClickable && addressSearchParams.zipcode ? 'Search Property' : '',
        onClick:
          is_searchable && addressSearchParams.zipcode
            ? handleClick
            : undefined,
      }}
      cardStyles={{ mb: 0 }}
      customTitle={addressCardTitle}
    >
      <DataList
        items={[
          ['Address type', address.type || ''],
          ['Dates seen', datesSeen],
        ]}
      />
    </DataCard>
  );
};
